import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, KeyRound, Upload } from "lucide-react";

const copyText = (t, what) => { navigator.clipboard.writeText(t); toast.success(`${what} copied`); };

function LoginForm({ clientId, initial, onSaved, onCancel }) {
  const [u, setU] = useState(initial || "");
  const [p, setP] = useState("");
  const save = async () => {
    try { await api.put(`/clients/${clientId}/portal-login`, { username: u, password: p || null }); toast.success("Portal login saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cls = "rounded-md border border-border bg-background px-3 py-1.5 text-sm font-mono";
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="portal-login-form">
      <input data-testid="portal-login-username-input" value={u} onChange={(e) => setU(e.target.value)} placeholder="Portal User ID" className={cls} />
      <input data-testid="portal-login-password-input" type="password" value={p} onChange={(e) => setP(e.target.value)} placeholder={initial ? "New password (blank = keep)" : "Portal password"} className={cls} autoComplete="new-password" />
      <button data-testid="portal-login-save-button" disabled={!u.trim()} onClick={save} className="rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium">Save</button>
      {onCancel && <button onClick={onCancel} className="rounded-md border border-border px-3 py-2 text-xs" data-testid="portal-login-cancel-button">Cancel</button>}
    </div>
  );
}

export function PortalLoginBox({ clientId, role }) {
  const [lg, setLg] = useState(null);
  const [edit, setEdit] = useState(false);
  const [pw, setPw] = useState("");
  const load = useCallback(() => api.get(`/clients/${clientId}/portal-login`).then((r) => setLg(r.data)).catch(() => {}), [clientId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!pw) { return undefined; } const t = setTimeout(() => setPw(""), 30000); return () => clearTimeout(t); }, [pw]);
  const canEdit = role !== "reader";
  const canReveal = role === "admin" || role === "partner";
  const reveal = async () => {
    try { const { data } = await api.post(`/clients/${clientId}/portal-login/reveal`); setPw(data.password); } catch (e) { toast.error(apiError(e)); }
  };
  if (!lg) { return null; }
  if (edit || (!lg.username && canEdit)) {
    return <LoginForm clientId={clientId} initial={lg.username} onSaved={async () => { setPw(""); await load(); setEdit(false); }} onCancel={lg.username ? () => setEdit(false) : null} />;
  }
  if (!lg.username) { return <p className="text-xs text-muted-foreground">No portal User ID saved yet.</p>; }
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="portal-login-box">
      <KeyRound className="h-4 w-4 text-muted-foreground" />
      <button data-testid="portal-copy-username-button" onClick={() => copyText(lg.username, "User ID")} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 font-mono hover:bg-muted">
        <Copy className="h-3.5 w-3.5" /> User ID: {lg.username}
      </button>
      {lg.has_password && canReveal && (pw
        ? <><button data-testid="portal-copy-password-button" onClick={() => copyText(pw, "Password")} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 font-mono hover:bg-muted"><Copy className="h-3.5 w-3.5" /> <span data-testid="portal-password-value">{pw}</span></button>
            <button onClick={() => setPw("")} className="p-1.5 rounded hover:bg-muted" data-testid="portal-hide-password-button" title="Hide"><EyeOff className="h-3.5 w-3.5" /></button></>
        : <button data-testid="portal-reveal-password-button" onClick={reveal} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 hover:bg-muted"><Eye className="h-3.5 w-3.5" /> Show password</button>)}
      {lg.has_password && !canReveal && <span className="text-muted-foreground">Password saved (Admin / Partner can view)</span>}
      {!lg.has_password && <span className="text-amber-600">No password saved</span>}
      {canEdit && <button data-testid="portal-login-edit-button" onClick={() => { setPw(""); setEdit(true); }} className="text-indigo-600 hover:underline">Change</button>}
    </div>
  );
}

export function PortalLoginsUpload() {
  const ref = useRef(null);
  const upload = async (f) => {
    if (!f) { return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/portal-logins/import", fd);
      const msg = `Portal logins saved for ${data.saved} client(s)${data.error_count ? `, ${data.error_count} row(s) skipped` : ""}`;
      if (data.error_count) { toast.warning(msg, { description: data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.error}`).join(" · ") }); } else { toast.success(msg); }
    } catch (e) { toast.error(apiError(e)); }
    ref.current.value = "";
  };
  return (
    <>
      <button data-testid="portal-logins-upload-button" onClick={() => ref.current.click()} title="Client Credentials sheet: GSTIN No, Client Name, User id, State Code (+ optional Password column)"
        className="flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"><Upload className="h-4 w-4" /> Upload portal logins</button>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" data-testid="portal-logins-upload-input" onChange={(e) => upload(e.target.files[0])} />
    </>
  );
}
